import React, { createContext, useMemo } from "react";

// === FRAME CONTEXT ===
export interface FrameState {
    currentFrameUploadTextPromptProposals: string[] | undefined;
    selectedAnnotationId: string | null;
    hoveredAnnotationId: string | null;
    shouldShowAnnotationInput: boolean;
    orderedTimelineFramePreviewUrls: { [fileUuid: string]: { [numFileFrames: number]: string[] } }; // numFileFrames is used to support video trimming
}

export interface FrameActions {
    setCurrentFrameUploadTextPromptProposals: React.Dispatch<
        React.SetStateAction<string[] | undefined>
    >;
    setSelectedAnnotationId: React.Dispatch<React.SetStateAction<string | null>>;
    setHoveredAnnotationId: React.Dispatch<React.SetStateAction<string | null>>;
    setShouldShowAnnotationInput: React.Dispatch<React.SetStateAction<boolean>>;
    setOrderedTimelineFramePreviewUrls: React.Dispatch<
        React.SetStateAction<{ [fileUuid: string]: { [numFileFrames: number]: string[] } }>
    >;
    /**
     * Request the video player (if any) to seek to a specific timestamp. The implementation
     * lives in components that actually render a player – the context just exposes the API.
     */
    requestSeekToTime: (time: number) => void;
    cancelAutoPredictions: () => void;
    cancelAutoPredictionsRef: React.RefObject<(() => void) | null>;
}

export interface FrameContextType extends FrameState, FrameActions {}

export const FrameContext = createContext<FrameContextType | undefined>(undefined);

export function useFrame(): FrameContextType {
    const context = React.useContext(FrameContext);
    if (!context) {
        throw new Error("useFrame must be used within a FrameProvider");
    }
    return context;
}

// Hook that avoids frame change re-renders (for UI components)
export function useFrameStable() {
    const context = useFrame();
    if (!context) {
        throw new Error("useFrameStable must be used within a FrameProvider");
    }

    return useMemo(
        () => ({
            selectedAnnotationId: context.selectedAnnotationId,
            setSelectedAnnotationId: context.setSelectedAnnotationId,
            shouldShowAnnotationInput: context.shouldShowAnnotationInput,
            setShouldShowAnnotationInput: context.setShouldShowAnnotationInput,
            orderedTimelineFramePreviewUrls: context.orderedTimelineFramePreviewUrls,
            setOrderedTimelineFramePreviewUrls: context.setOrderedTimelineFramePreviewUrls,
            requestSeekToTime: context.requestSeekToTime
        }),
        [
            context.selectedAnnotationId,
            context.setSelectedAnnotationId,
            context.shouldShowAnnotationInput,
            context.setShouldShowAnnotationInput,
            context.orderedTimelineFramePreviewUrls,
            context.setOrderedTimelineFramePreviewUrls,
            context.requestSeekToTime
        ]
    );
}
